import React, { createContext, useContext, useState, useEffect } from 'react';

const STORAGE_THEME = 'gsradio-theme';
const STORAGE_LANG = 'gsradio-lang';

const SUPPORTED_LANGS = ['he', 'en', 'ru', 'ar', 'am'];
const FALLBACK_LANG = 'he';

/** Map browser language code to our supported code, or null */
function mapBrowserToSupported(browserLang) {
  if (!browserLang || typeof browserLang !== 'string') return null;
  const primary = browserLang.split(/[-_]/)[0].toLowerCase();
  if (SUPPORTED_LANGS.includes(primary)) return primary;
  return null;
}

/** Initial language: stored preference, else device language if supported, else Hebrew */
function getInitialLang() {
  try {
    const stored = localStorage.getItem(STORAGE_LANG);
    if (stored && SUPPORTED_LANGS.includes(stored)) return stored;
    if (typeof navigator !== 'undefined') {
      const languages = navigator.languages && navigator.languages.length
        ? navigator.languages
        : [navigator.language];
      for (const lang of languages) {
        const mapped = mapBrowserToSupported(lang);
        if (mapped) return mapped;
      }
    }
  } catch (_) {}
  return FALLBACK_LANG;
}

const getInitialTheme = () => {
  try {
    return localStorage.getItem(STORAGE_THEME) || 'dark';
  } catch {
    return 'dark';
  }
};

const ThemeLanguageContext = createContext(null);

export function ThemeLanguageProvider({ children }) {
  const [theme, setThemeState] = useState(getInitialTheme);
  const [language, setLanguageState] = useState(getInitialLang);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_THEME, theme);
    } catch (_) {}
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_LANG, language);
    } catch (_) {}
    const dir = (language === 'he' || language === 'ar') ? 'rtl' : 'ltr';
    document.documentElement.setAttribute('dir', dir);
    document.body.setAttribute('dir', dir);
  }, [language]);

  const setTheme = (v) => setThemeState(v);
  const setLanguage = (v) => setLanguageState(v);

  return (
    <ThemeLanguageContext.Provider
      value={{
        theme,
        setTheme,
        language,
        setLanguage,
      }}
    >
      {children}
    </ThemeLanguageContext.Provider>
  );
}

export function useThemeLanguage() {
  const ctx = useContext(ThemeLanguageContext);
  if (!ctx) {
    throw new Error('useThemeLanguage must be used within ThemeLanguageProvider');
  }
  return ctx;
}
